import ScrollModal from "@/components/common/scroll-modal";
import { DialogDescription, DialogTitle } from "@/components/ui/dialog";
import {
  Avatar,
  AvatarFallback,
  Button,
  FileItem,
  Heading,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import { useEffect, useRef, useState } from "react";
import { uploadFile } from "@/queries/process";
import type { ProcessFileType } from "@/types/entities/process";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { ConfirmationModalBase } from "@/components/common/modals";

interface EditTDSModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  files: ProcessFileType[];
  processId?: string;
}

export const EditTDSModal = ({
  isOpen = false,
  onClose = () => {},
  onSuccess = () => {},
  processId,
  files,
}: EditTDSModalProps) => {
  const { hasPermission } = useAuthorization();
  const canEdit = hasPermission(Permissions.flows.update);
  const [isLoading, setisLoading] = useState(false);
  const [newFile, setNewFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [error, setError] = useState<{
    message: string;
    fileName: string;
  } | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const uploadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { showToast } = useToast();

  const currentFile = files.length > 0 ? files[files.length - 1] : null;
  const pastFiles = files.slice(0, -1).reverse();
  const handleOpenFile = (fileUrl: string) => {
    try {
      if (!fileUrl) {
        showToast({
          title: "Error",
          description: "La URL del archivo no está disponible",
          options: {
            type: "warning",
          },
        });
        return;
      }
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    } catch {
      showToast({
        title: "Error",
        description: "Error al abrir el archivo",
        options: {
          type: "error",
        },
      });
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);

    // Check file size (25 MB limit)
    const maxSizeInBytes = 25 * 1024 * 1024; // 25 MB
    if (file.size > maxSizeInBytes) {
      setError({
        message: "El archivo excede el tamaño máximo permitido de 25 MB",
        fileName: file.name,
      });
      setNewFile(null);
      event.target.value = "";
      return;
    }

    const validExtensions = [
      ".pdf",
      ".docx",
      ".xlsx",
      ".png",
      ".jpg",
      ".jpeg",
      ".zip",
    ];
    const fileExtension = file.name
      .slice(file.name.lastIndexOf("."))
      .toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      setError({
        message:
          "Por favor selecciona un archivo válido (PDF, DOCX, XLSX, PNG, JPG o ZIP)",
        fileName: file.name,
      });
      setNewFile(null);
      event.target.value = "";
      return;
    }

    setNewFile(file);
    setIsUploading(true);
    setUploadProgress(0);

    const totalDuration = 1000; // 1 second
    const steps = 100;
    const intervalTime = totalDuration / steps;

    // Clear any existing interval before creating a new one
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    progressIntervalRef.current = setInterval(() => {
      setUploadProgress((prev) => {
        const nextValue = prev + 1;
        if (nextValue >= 100) {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }

          uploadTimeoutRef.current = setTimeout(() => {
            setIsUploading(false);
            uploadTimeoutRef.current = null;
          }, 300);

          return 100;
        }
        return nextValue;
      });
    }, intervalTime);
  };

  const clearFile = () => {
    setNewFile(null);
    setError(null);
    setUploadProgress(0);
    setIsUploading(false);

    // Clean up any running timers
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (uploadTimeoutRef.current) {
      clearTimeout(uploadTimeoutRef.current);
      uploadTimeoutRef.current = null;
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setisLoading(true);
    if (newFile && processId) {
      try {
        await uploadFile({
          file: newFile,
          entity_id: processId,
          entity_type: "process",
        });
        showToast({
          title: "Cambios guardados",
          description:
            "Los cambios realizados en la tabla se guardaron correctamente.",
          options: {
            type: "success",
            duration: 5000,
            customIcon: (
              <Avatar size="xs" shape="square">
                <AvatarFallback color="action" shape="square" type="icon">
                  <Icon type="fas" name="save" />
                </AvatarFallback>
              </Avatar>
            ),
          },
        });
      } catch (uploadError) {
        console.error("Error uploading file:", uploadError);
      }
    }
    setisLoading(false);
    onSuccess();
  };

  useEffect(() => {
    if (!isOpen) {
      clearFile();
    }
  }, [isOpen]);

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    if (isLoading || newFile || isUploading) {
      setConfirmClose(true);
    } else {
      onClose();
    }
  };

  return (
    <>
      <ConfirmationModalBase
        isOpen={confirmClose}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        onAccept={() => {
          setConfirmClose(false);
          onClose();
        }}
        onClose={() => {
          setConfirmClose(false);
        }}
      />
      <ScrollModal
        isOpen={isOpen}
        onClose={handleClose}
        modalContentClassName="w-full max-w-[520px] max-h-[680px] h-full flex flex-col"
        header={
          <div className="flex items-center justify-between px-8 py-3">
            <DialogTitle>Ficha Técnica</DialogTitle>
            <DialogDescription className="sr-only">
              Formulario para editar una ficha técnica
            </DialogDescription>
            <Button
              type="button"
              onClick={handleClose}
              variant="quiet"
              aria-label="Cerrar"
              className="w-8 h-8 cursor-pointer"
            >
              <Icon type="far" name="times" />
            </Button>
          </div>
        }
        footer={
          canEdit && (
            <div className="w-full grid grid-cols-1 tablet:grid-cols-2 py-3 px-8 gap-3">
              <Button
                variant="tertiary"
                size="sm"
                type="button"
                onClick={handleClose}
                className="min-w-[120px] cursor-pointer"
              >
                Cancelar
              </Button>
              <Button
                variant="default"
                size="sm"
                type="submit"
                disabled={isLoading || !newFile || isUploading}
                form="tds-form"
                className="min-w-[120px] cursor-pointer"
              >
                Guardar Cambios
                {isLoading && (
                  <Icon
                    type="fas"
                    name="spinner-third"
                    className="animate-spin"
                  />
                )}
              </Button>
            </div>
          )
        }
      >
        <form
          id="tds-form"
          data-testid="new-table-form"
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 min-h-0 px-4 sm:px-8"
        >
          {/* Contenido scrollable */}
          <div className="flex flex-col gap-6 pb-6">
            <>
              {/* Detalles */}
              <div className="flex flex-col gap-4">
                <Heading variant="h6">Ficha Técnica Actual</Heading>
                {newFile || error ? (
                  <FileItem
                    fileName={newFile?.name || error?.fileName || ""}
                    tag={!error ? `v${files.length + 1}` : undefined}
                    color="action"
                    showProgress={isUploading}
                    progressValue={uploadProgress}
                    actionAriaLabel="Eliminar ficha"
                    action={
                      !isUploading && <Icon type="far" name="times" size="sm" />
                    }
                    onAction={clearFile}
                    error={error?.message}
                  />
                ) : (
                  currentFile && (
                    <FileItem
                      fileName={currentFile.file_name}
                      tag={`v${files.length}`}
                      color="action"
                      action={
                        <Icon type="far" name="arrow-up-right" size="sm" />
                      }
                      actionAriaLabel={"Ver ficha"}
                      onAction={() => handleOpenFile(currentFile.url)}
                    />
                  )
                )}

                {canEdit && (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.zip"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <Button
                      variant="quiet"
                      size="xs"
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      <Icon type="far" name="arrow-up-from-bracket" size="sm" />
                      {currentFile ? "Reemplazar ficha" : "Subir ficha"}
                    </Button>
                  </div>
                )}
              </div>
              {/* Past Files Section */}
              {canEdit && pastFiles.length > 0 && (
                <>
                  <hr className="border-neutral" />
                  <div className="flex flex-col gap-4">
                    <Heading variant="h6">Fichas Técnicas Pasadas</Heading>
                    <div
                      ref={scrollContainerRef}
                      className="flex flex-col gap-2 max-h-60 overflow-y-auto"
                    >
                      {pastFiles.map((file, index) => (
                        <FileItem
                          key={`${file.id}-${index}`}
                          fileName={file.file_name}
                          tag={
                            files.length > 0
                              ? `v${files.length - 1 - index}`
                              : undefined
                          }
                          action={
                            <Icon type="far" name="arrow-up-right" size="sm" />
                          }
                          actionAriaLabel="Ver ficha"
                          onAction={() => handleOpenFile(file.url)}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          </div>
        </form>
      </ScrollModal>
    </>
  );
};
